import React from 'react';
import { MapPin, Car, Train, Navigation, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

interface NavigationButtonsProps {
  /**
   * Provider location (e.g., "Brussel, België" or "Antwerpen, België")
   */
  location: string;
  /**
   * Full address if available (optional, will use location as fallback)
   */
  address?: string;
  /**
   * Service type to determine if location is relevant
   */
  serviceType?: 'online' | 'in-person' | 'both';
}

const NavigationButtons: React.FC<NavigationButtonsProps> = ({ 
  location, 
  address, 
  serviceType = 'in-person' 
}) => {
  // Don't show navigation for online-only services
  if (serviceType === 'online') {
    return null;
  }

  // Use address if available, otherwise fall back to location
  const destination = address || location;
  const encodedDestination = encodeURIComponent(destination);

  // Belgium-specific navigation functions
  const openGoogleMaps = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodedDestination}`;
    console.log('Opening Google Maps:', url);
    window.open(url, '_blank');
    toast.success('Google Maps geopend');
  };

  const openWaze = () => {
    const url = `https://waze.com/ul?q=${encodedDestination}`;
    console.log('Opening Waze:', url);
    window.open(url, '_blank');
    toast.success('Waze geopend');
  };

  const openNMBS = () => {
    const url = `https://www.belgiantrain.be/nl/travel-info/prepare-for-your-journey/route-planner`;
    console.log('Opening NMBS Journey Planner:', url);
    window.open(url, '_blank');
    toast.success('NMBS Routeplanner geopend');
  };

  const openAppleMaps = () => {
    const url = `http://maps.apple.com/?q=${encodedDestination}`;
    console.log('Opening Apple Maps:', url);
    window.open(url, '_blank');
    toast.success('Apple Maps geopend');
  };

  return (
    <Card className="shadow-sm" style={{ 
      borderColor: '#efe7dd', 
      backgroundColor: '#fbf8f4' 
    }}>
      <CardHeader className="pb-4">
        <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
          <Navigation className="h-5 w-5" style={{ color: '#b6a092' }} />
          Route Plannen
        </CardTitle>
        <div className="flex items-center gap-2 text-sm" style={{ color: '#a0897c' }}>
          <MapPin className="h-4 w-4" style={{ color: '#b6a092' }} />
          <span>{destination}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Primary Navigation Options */}
        <div className="grid grid-cols-2 gap-3">
          {/* Google Maps */}
          <Button
            onClick={openGoogleMaps}
            variant="outline"
            className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-beige-100"
            style={{ 
              borderColor: '#e6dbd0',
              color: '#837069'
            }}
          >
            <Car className="h-5 w-5" style={{ color: '#b6a092' }} />
            <div className="text-xs text-center">
              <div className="font-medium">Google Maps</div>
              <div style={{ color: '#b6a092' }}>Auto navigatie</div>
            </div>
            <ExternalLink className="h-3 w-3" style={{ color: '#b6a092' }} />
          </Button>

          {/* Waze */}
          <Button
            onClick={openWaze}
            variant="outline"
            className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-beige-100"
            style={{ 
              borderColor: '#e6dbd0',
              color: '#837069'
            }}
          >
            <Navigation className="h-5 w-5" style={{ color: '#b6a092' }} />
            <div className="text-xs text-center">
              <div className="font-medium">Waze</div>
              <div style={{ color: '#b6a092' }}>Verkeer & routes</div>
            </div>
            <ExternalLink className="h-3 w-3" style={{ color: '#b6a092' }} />
          </Button>
        </div>

        {/* Secondary Navigation Options */}
        <div className="grid grid-cols-2 gap-3">
          {/* NMBS/SNCB Public Transport */}
          <Button
            onClick={openNMBS}
            variant="outline"
            className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-beige-100"
            style={{ 
              borderColor: '#e6dbd0',
              color: '#837069'
            }}
          >
            <Train className="h-5 w-5" style={{ color: '#b6a092' }} />
            <div className="text-xs text-center">
              <div className="font-medium">NMBS</div>
              <div style={{ color: '#b6a092' }}>Openbaar vervoer</div>
            </div>
            <ExternalLink className="h-3 w-3" style={{ color: '#b6a092' }} />
          </Button>

          {/* Apple Maps */}
          <Button
            onClick={openAppleMaps}
            variant="outline"
            className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-beige-100"
            style={{ 
              borderColor: '#e6dbd0',
              color: '#837069'
            }}
          >
            <MapPin className="h-5 w-5" style={{ color: '#b6a092' }} />
            <div className="text-xs text-center">
              <div className="font-medium">Apple Maps</div>
              <div style={{ color: '#b6a092' }}>iOS navigatie</div>
            </div>
            <ExternalLink className="h-3 w-3" style={{ color: '#b6a092' }} />
          </Button>
        </div>

        {/* Info Text */}
        <div className="rounded-lg p-3 text-center" style={{ 
          backgroundColor: '#efe7dd', 
          borderColor: '#e6dbd0',
          border: '1px solid'
        }}>
          <p className="text-xs" style={{ color: '#837069' }}>
            Kies je favoriete navigatie app om de route te plannen naar deze locatie
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default NavigationButtons;